import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
} from "react";
import { createPortal } from "react-dom";
import styled from "@emotion/styled";
import { ODSButton } from "@telekom-ods/react-ui-kit/ODSButton";
import { useWidgetStore } from "@/state";
import { Avatar, VisuallyHidden } from "@/components";
import { colors } from "@/styling";
import type { TFunction } from "i18next";
import { markWidgetDismissed } from "@/bootstrap";
import { Close, TelekomID } from "@/assets";
import { isFullViewportAnchor, computeAnchoredPopoverPosition } from "@/utils";

const AccountButton = styled("button")({
  cursor: "pointer",
  border: 0,
  padding: 0,
  background: "transparent",
  outline: "none",
  width: 28,
  height: 28,
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",

  "&:focus-visible": {
    outline: `2px solid ${colors.comet}`,
    outlineOffset: 2,
    borderRadius: 4,
  },
});

const PopoverBackdrop = styled.div({
  position: "fixed",
  inset: 0,
  boxSizing: "border-box",
  backgroundColor: "rgba(0, 0, 0, 0.28)",
  zIndex: 2147483000,
});

const AccountCard = styled.div({
  position: "fixed",
  zIndex: 2147483001,
  width: "min(300px, calc(100vw - 16px))",
  boxSizing: "border-box",
  borderRadius: 24,
  background:
    "var(--colours-basic-background-card, var(--colours-basic-background))",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.14)",
  padding: "28px 22px 22px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const PanelCloseButton = styled("button")({
  position: "absolute",
  top: 10,
  right: 10,
  zIndex: 2,
  cursor: "pointer",
  border: 0,
  padding: 6,
  margin: 0,
  width: 36,
  height: 36,
  borderRadius: 8,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "transparent",
  color: colors.cello,
  outline: "none",

  "&:focus-visible": {
    outline: `2px solid ${colors.comet}`,
    outlineOffset: 2,
    borderRadius: 8,
  },

  "& svg": {
    width: 20,
    height: 20,
    display: "block",
  },
});

const AvatarWrap = styled.div({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
});

const BrandLogo = styled.div({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  marginTop: "36px",
  marginBottom: "36px",

  "& svg": {
    maxWidth: "100%",
    height: "auto",
    display: "block",
  },
});

const ActionsStack = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: 20,
  width: "100%",
  alignItems: "stretch",
});

interface DropDownProps {
  t: TFunction;
  isAuto: boolean;
}

const DropDown = ({ t, isAuto }: DropDownProps) => {
  const st = useWidgetStore((s) => s.state);
  const actions = useWidgetStore((s) => s.actions);

  const portalContainer = useWidgetStore((s) => s.portalContainer);

  const menuAnchorEl = useWidgetStore((s) => s.menuAnchorEl);
  const setMenuAnchorEl = useWidgetStore((s) => s.setMenuAnchorEl);
  const setUiVisible = useWidgetStore((s) => s.setUiVisible);

  const setTriggerEl = useWidgetStore((s) => s.setTriggerEl);

  const reactId = useId();
  const triggerId = useMemo(() => `cosid-trigger-${reactId}`, [reactId]);
  const menuId = useMemo(() => `cosid-menu-${reactId}`, [reactId]);
  const titleId = useMemo(() => `cosid-panel-title-${reactId}`, [reactId]);

  const lastOutsideDismissRef = useRef(0);
  const isOpen = Boolean(menuAnchorEl);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const [cardPos, setCardPos] = useState<{ top: number; left: number } | null>(
    null,
  );

  const closeMenu = useCallback(() => {
    markWidgetDismissed();
    setMenuAnchorEl(null);

    window.setTimeout(() => {
      setUiVisible(false);
    }, 0);
  }, [setMenuAnchorEl, setUiVisible]);

  const onPointerDownCapture = useCallback(
    (ev: PointerEvent) => {
      const snap = useWidgetStore.getState();

      const panel = panelRef.current;
      const trigger = isAuto ? snap.triggerEl : null;
      const anchor = snap.menuAnchorEl;

      const path = (ev.composedPath?.() ?? []) as EventTarget[];

      const insidePanel = !!panel && path.includes(panel);
      const insideTrigger = isAuto && !!trigger && path.includes(trigger);
      const insideAnchor =
        !!anchor && !isFullViewportAnchor(anchor) && path.includes(anchor);

      if (!insidePanel && !insideTrigger && !insideAnchor) {
        lastOutsideDismissRef.current = performance.now();
        closeMenu();
      }
    },
    [closeMenu, isAuto],
  );

  useLayoutEffect(() => {
    if (!isOpen || !st.loggedIn || !menuAnchorEl) {
      setCardPos(null);
      return;
    }

    const anchor = menuAnchorEl;

    const updatePosition = () => {
      const panel = panelRef.current;
      if (!panel) return;
      const w = panel.offsetWidth;
      const h = panel.offsetHeight;
      if (w === 0 || h === 0) return;
      setCardPos(computeAnchoredPopoverPosition(anchor, w, h));
    };

    updatePosition();
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      updatePosition();
      raf2 = requestAnimationFrame(updatePosition);
    });

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, st.loggedIn, menuAnchorEl]);

  useEffect(() => {
    if (!isOpen) return;

    window.addEventListener("pointerdown", onPointerDownCapture, true);
    return () =>
      window.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, [isOpen, onPointerDownCapture]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeMenu();
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [isOpen, closeMenu]);

  useEffect(() => {
    if (!isOpen || !st.loggedIn || !cardPos || !panelRef.current) return;
    const first = panelRef.current.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    window.requestAnimationFrame(() => first?.focus());
  }, [isOpen, st.loggedIn, cardPos]);

  const handleTriggerClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!isAuto) return;

    const now = performance.now();
    if (now - lastOutsideDismissRef.current < 250) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (!st.loggedIn) {
      actions.loginFn(e);
      return;
    }

    if (isOpen) {
      closeMenu();
      return;
    }

    setUiVisible(true);
    setMenuAnchorEl(e.currentTarget);
  };

  const handleProfile = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    closeMenu();
    actions.gotoProfileFn(e);
  };

  const handleRelogin = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    closeMenu();
    actions.reLoginFn(e);
  };

  const handleLogout = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    closeMenu();
    actions.logoutFn(e);
  };

  const handleClosePanel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    closeMenu();
  };

  const triggerLabel = st.loggedIn
    ? t("dropdown.account")
    : t("dropdown.signIn");

  const avatarSrc = st.loggedIn ? st.user?.avatarUrl : undefined;
  // Logged-out avatar override (`data-avatarUrl`) temporarily disabled:
  // : st.avatarUrl?.trim() || undefined;

  const portalRoot = portalContainer ?? document.body;

  const btnFull: CSSProperties = {
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  };

  const panel =
    isOpen && st.loggedIn ? (
      <>
        <PopoverBackdrop
          role="presentation"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        />
        <AccountCard
          id={menuId}
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          data-scheme="neutral"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          style={
            cardPos
              ? { top: cardPos.top, left: cardPos.left, visibility: "visible" }
              : { visibility: "hidden", top: 0, left: 0 }
          }
        >
          <AvatarWrap>
            <Avatar src={avatarSrc} size={72} />
          </AvatarWrap>

          <VisuallyHidden id={titleId}>
            {t("dropdown.telekomIdTitle")}
          </VisuallyHidden>
          <BrandLogo aria-hidden>
            <TelekomID />
          </BrandLogo>

          <ActionsStack>
            <ODSButton
              type="button"
              variant="outline"
              size="small"
              textWrap
              label={t("dropdown.profileSettings")}
              onClick={handleProfile}
              style={btnFull}
            />
            <ODSButton
              type="button"
              variant="outline"
              size="small"
              textWrap
              label={t("dropdown.signInWithAnother")}
              onClick={handleRelogin}
              style={btnFull}
            />
            <ODSButton
              type="button"
              variant="secondary"
              size="small"
              textWrap
              label={t("dropdown.logout")}
              onClick={handleLogout}
              style={btnFull}
            />
          </ActionsStack>

          <PanelCloseButton
            type="button"
            onClick={handleClosePanel}
            aria-label={t("dropdown.close")}
          >
            <Close aria-hidden />
          </PanelCloseButton>
        </AccountCard>
      </>
    ) : null;

  return (
    <div style={{ display: "flex" }}>
      {isAuto && (
        <AccountButton
          id={triggerId}
          ref={setTriggerEl}
          type="button"
          onClick={handleTriggerClick}
          aria-label={triggerLabel}
          aria-haspopup="dialog"
          aria-controls={isOpen ? menuId : undefined}
          aria-expanded={isOpen ? "true" : "false"}
        >
          <Avatar src={avatarSrc} size={28} />
          <VisuallyHidden>{triggerLabel}</VisuallyHidden>
        </AccountButton>
      )}

      {panel ? createPortal(panel, portalRoot) : null}
    </div>
  );
};

export default DropDown;
